// src/pages/TopIssuerPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useAuth } from '../context/AuthContext';

import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule]);

const generateMonthOptions = () => {
  const options = [];
  const d = new Date();
  const stop = new Date(2025, 0, 1); // January 2025
  while (d >= stop) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    options.push({
      value: `${year}-${month}`,
      label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
    d.setMonth(d.getMonth() - 1);
  }
  return options;
};

const tzOptions = {
  timeZone: 'Asia/Jakarta',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

function formatDateToISO(date) {
  const formattedDate = new Intl.DateTimeFormat('en-GB', tzOptions).format(date);
  const [day, month, year] = formattedDate.split('/');
  return `${year}-${month}-${day}`;
}

const formatNumber = (params) => {
  if (params.value == null || isNaN(params.value)) return '';
  return new Intl.NumberFormat('id-ID').format(params.value);
};

function TopIssuerPage() {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [isGridReady, setIsGridReady] = useState(false);
  const [vendors, setVendors] = useState([]);
  const auth = useAuth();

  const getToday = new Date();
  const getFirstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const today = formatDateToISO(getToday);
  const firstDayOfMonth = formatDateToISO(getFirstDayOfMonth);

  const [filterType, setFilterType] = useState('daily');
  const [selectedMonth, setSelectedMonth] = useState('');
  const monthOptions = generateMonthOptions();

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
    vendor: '',
    group_by: 'payment_issuer',
    display_by: 'product_name',
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = async () => {
    // Clear data first to prevent stale rows
    setRowData([]);
    setColumnDefs([]);

    let finalFilters = { ...filters };

    if (filterType === 'monthly' && selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      finalFilters.start_date = formatDateToISO(startDate);
      finalFilters.end_date = formatDateToISO(endDate);
    }

    const params = new URLSearchParams(finalFilters);
    const apiUrl = `/api/top-issuer/pivot?${params.toString()}`;

    try {
      const response = await auth.authFetch(apiUrl);
      const data = await response.json();

      if (data.columns && data.data) {
        // Determine which columns are "pinned" label columns
        const labelCols = filters.group_by === 'pga' 
          ? ['pga_name', 'vendor', 'agent', 'project'] 
          : ['payment_issuer'];

        const dynamicColumns = data.columns.map(col => {
          const colStr = col.toString();
          let colDef = {
            field: colStr,
            headerName: colStr.toUpperCase(),
          };

          if (labelCols.includes(col)) {
            colDef = { ...colDef, pinned: 'left', filter: true, width: col === 'payment_issuer' ? 250 : 150 };
          } else if (col === 'Total') {
            colDef = {
              ...colDef,
              width: 150,
              valueFormatter: formatNumber,
              cellStyle: { textAlign: 'right', fontWeight: 'bold' },
              pinned: 'left'
            };
          } else {
            colDef = {
              ...colDef,
              width: 130,
              valueFormatter: formatNumber,
              cellStyle: { textAlign: 'right' }
            };
          }

          return colDef;
        });

        setColumnDefs(dynamicColumns);
        setRowData(data.data);
      } else {
        setColumnDefs([]);
        setRowData([]);
      }
    } catch (error) {
      console.error("Gagal mengambil data top issuer:", error);
      setColumnDefs([]);
      setRowData([]);
    } finally {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.hideOverlay();
      }
    }
  };

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await auth.authFetch('/api/top-issuer/vendors');
        const data = await response.json();
        setVendors(data);
      } catch (error) {
        console.error("Gagal mengambil data vendor:", error);
      }
    };

    fetchVendors();
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onExportClick = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.exportDataAsCsv({
      fileName: `top_issuer_${filters.group_by}_${filters.start_date}_${filters.end_date}.csv`,
      suppressQuotes: true,
      processCellCallback: (params) => {
        const rawValue = params.value;
        if (typeof rawValue === 'string') {
          return rawValue;
        }
        return rawValue;
      }
    });
  };

  const onGridReady = useCallback(() => {
    setIsGridReady(true);
  }, []);

  const onGridPreDestroyed = useCallback(() => {
    setIsGridReady(false);
  }, []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
  }), []);

  const getRowStyle = params => {
    const firstCol = filters.group_by === 'pga' ? 'pga_name' : 'payment_issuer';
    if (params.data?.[firstCol] === 'Total') {
      return { fontWeight: 'bold', backgroundColor: '#343a40', color: 'white' };
    }
  };

  const getRowId = params => {
    if (filters.group_by === 'pga') {
      if (params.data?.pga_name === 'Total') return 'TotalRow';
      return (params.data?.pga_name ?? '') + (params.data?.vendor ?? '') + (params.data?.agent ?? '');
    } else {
      if (params.data?.payment_issuer === 'Total') return 'TotalRow';
      return params.data?.payment_issuer ?? '';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <h1>Top Issuer</h1>

      <div className="pivot-filters" style={{ gap: 8, display: 'flex', flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label>
            <input
              type="radio"
              value="daily"
              checked={filterType === 'daily'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            {' '}Harian
          </label>
          <label>
            <input
              type="radio"
              value="monthly"
              checked={filterType === 'monthly'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            {' '}Bulanan
          </label>
        </div>

        {filterType === 'daily' && (
          <>
            <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} />
            <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} />
          </>
        )}

        {filterType === 'monthly' && (
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Pilih Bulan</option>
            {monthOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}

        <select name="vendor" value={filters.vendor} onChange={handleFilterChange}>
          <option value="">All Vendors</option>
          {vendors.map(vendor => (
            <option key={vendor} value={vendor}>{vendor}</option>
          ))}
        </select>

        <select name="group_by" value={filters.group_by} onChange={handleFilterChange}>
          <option value="payment_issuer">Group by Payment Issuer</option>
          <option value="pga">Group by PGA</option>
        </select>

        <select name="display_by" value={filters.display_by} onChange={handleFilterChange}>
          <option value="product_name">Tampilkan: Nama Produk</option>
          <option value="provider_name">Tampilkan: Nama Penyelenggara</option>
        </select>

        <button onClick={applyFilters}>Apply Filter</button>
        <button onClick={onExportClick} disabled={!isGridReady || rowData.length === 0}>Export to CSV</button>
      </div>

      <div className="ag-theme-alpine" style={{ flex: 1, width: '100%' }}>
        <AgGridReact
          key={filters.group_by}
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          getRowId={getRowId}
          onGridReady={onGridReady}
          onGridPreDestroyed={onGridPreDestroyed}
          overlayNoRowsTemplate="<span>No data found. Try syncing Top Issuer data first.</span>"
          overlayLoadingTemplate="<span>Loading...</span>"
        />
      </div>
    </div>
  );
}

export default TopIssuerPage;
